import { Link } from "react-router";

import {
  CalendarClockIcon,
  ClapperboardIcon,
  FingerprintIcon,
  MapPinIcon,
  TicketIcon,
} from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  EmptyHint,
  PriceCheckedAt,
  MetaDot,
  RawJsonCard,
  ShowHero,
  SystemSection,
  fmtDateTime,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import {
  ScreeningViewType,
  screeningViewSchema,
} from "@/pages/screenings/schemas/screening.view";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

export const ScreeningsShow = () => {
  const { record, raw } = useShowRecord<ScreeningViewType>(
    screeningViewSchema,
    (r) => `Screening | ${r.movie?.title ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={record.movie?.media?.photos?.[0]}
              eyebrow="Screening"
              title={record.movie?.title ?? "Untitled screening"}
              subtitle={record.venue?.name ?? undefined}
              meta={
                <>
                  <span className="inline-flex items-center gap-1">
                    <CalendarClockIcon className="size-3.5" />
                    {fmtDateTime(record.startDate)}
                  </span>
                  {record.endDate && (
                    <>
                      <MetaDot />
                      <span>until {fmtDateTime(record.endDate)}</span>
                    </>
                  )}
                </>
              }
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {record.format?.is3D && <Badge variant="outline">3D</Badge>}
                  {record.format?.language && (
                    <Badge variant="secondary">{record.format.language}</Badge>
                  )}
                  {record.format?.subtitles && (
                    <Badge variant="secondary">
                      CC {record.format.subtitles}
                    </Badge>
                  )}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                </>
              }
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Movie"
                icon={<ClapperboardIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Title">
                    {record.movie?.id ? (
                      <Link
                        to={`/movies/show/${record.movie.id}`}
                        className="text-primary hover:underline"
                      >
                        {record.movie.title}
                      </Link>
                    ) : (
                      record.movie?.title
                    )}
                  </DetailField>
                  <DetailField
                    label="Duration"
                    value={
                      record.movie?.durationInMinutes
                        ? `${record.movie.durationInMinutes} min`
                        : undefined
                    }
                  />
                  <DetailField label="Genres" full>
                    <Chips
                      items={(record.movie?.genres ?? []).map((g) => g.name)}
                    />
                  </DetailField>
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Venue"
                icon={<MapPinIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Name">
                    {record.venue?.id ? (
                      <Link
                        to={`/venues/edit/${record.venue.id}`}
                        className="text-primary hover:underline"
                      >
                        {record.venue.name}
                      </Link>
                    ) : (
                      record.venue?.name
                    )}
                  </DetailField>
                  <DetailField label="Area" value={record.venue?.area} />
                  <DetailField
                    label="Address"
                    value={record.venue?.address}
                    full
                  />
                  <DetailField
                    label="Timezone"
                    value={record.venue?.timezone}
                  />
                  <DetailField label="Status" value={record.venue?.status} />
                  <DetailField label="Maps" full>
                    {record.venue?.mapsUrl ? (
                      <a
                        href={record.venue.mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        Open in Maps
                      </a>
                    ) : undefined}
                  </DetailField>
                </DetailGrid>
              </DetailSection>
            </div>

            <DetailSection
              title="Pricing"
              icon={<TicketIcon className="size-4" />}
            >
              {record.pricing?.length ? (
                <ul className="divide-y rounded-lg border">
                  {record.pricing.map((p, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span>{p.label ?? "Standard"}</span>
                      <span className="font-medium">
                        {typeof p.amount === "number"
                          ? `${p.amount} ${p.currency ?? ""}`.trim()
                          : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyHint>No pricing tiers.</EmptyHint>
              )}
              <PriceCheckedAt value={record.priceCheckedAt} />
            </DetailSection>

            <DetailSection
              title="Details"
              icon={<FingerprintIcon className="size-4" />}
            >
              <DetailGrid>
                <DetailField label="ID" mono>
                  <span className="inline-flex items-center gap-1">
                    {record.id}
                    <CopyButton
                      text={record.id}
                      message="Copied screening ID"
                    />
                  </span>
                </DetailField>
                <DetailField
                  label="Starts"
                  value={fmtDateTime(record.startDate)}
                />
                <DetailField label="Ends" value={fmtDateTime(record.endDate)} />
                <DetailField label="Tags" full>
                  <Chips items={record.tags} variant="outline" />
                </DetailField>
              </DetailGrid>
            </DetailSection>

            <EnrichmentSummary
              resource="screenings"
              id={record.id}
              status={record.enrichmentStatus}
              enrichment={record.enrichment}
              bordered
            />

            {/* Tina — provenance, and the only non-SQL way to re-queue this
                entity's enrichment. Unlike
                artist/venue/geodata/event this entity has no per-entity enrich
                trigger anywhere in IMS, so `POST /enrichments` is not a second
                path to the same thing — it is the only one. */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <ReEnrichButton entityType="screening" entityId={record.id} />
              </div>
              <EntityTinaTrace
                entityType="screening"
                entityId={record.id}
                metadata={record.metadata}
                enrichment={record.enrichment}
              />
            </div>

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
